/**
 * useTaskCreationOptions - Lista categorias/fases filtradas por can_create
 *
 * Consome a RPC `get_task_creation_options` que respeita a permissão granular
 * por fase (task_phase_permissions.can_create). Admins recebem tudo; usuários
 * sem perfil recebem listas vazias.
 *
 * @version 1.2.0
 * RELEASE: 2026-06-09 v1.2.0 - Aceita `assignedTo` para considerar permissões
 *                              "Usuário Responsável" (is_responsible_only=true)
 *                              quando o responsável pretendido é o próprio
 *                              usuário autenticado.
 * RELEASE: 2026-06-03 v1.1.0 - Expõe flags `tenantHasPhases` e `isAdmin`.
 * RELEASE: 2026-06-02 v1.0.0 - Criação inicial
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useTaskModuleType } from '@/hooks/useTaskModuleType';

const logger = createLogger('useTaskCreationOptions');

/** Categoria reduzida (criação) */
export interface TaskCreationCategory {
  id: string;
  name: string;
  color: string;
  sort_order: number;
}

/** Fase reduzida (criação) */
export interface TaskCreationPhase {
  id: string;
  name: string;
  color: string;
  sort_order: number;
  category_id: string;
}

interface RpcResponse {
  categories: TaskCreationCategory[];
  phases_by_category: Record<string, TaskCreationPhase[]>;
  tenant_has_phases?: boolean;
  is_admin?: boolean;
  /** v1.3.0: categoria padrão do módulo (opcional — pode ser null) */
  default_category_id?: string | null;
  /** v1.3.0: fase padrão da categoria padrão (opcional — pode ser null) */
  default_phase_id?: string | null;
}

/**
 * Hook que retorna apenas categorias e fases nas quais o usuário corrente
 * tem permissão de criar tarefa.
 *
 * @param enabled - habilita ou desabilita a query (default: true)
 * @param assignedTo - responsável pretendido. Quando igual ao usuário atual,
 *   também libera fases configuradas como "Usuário Responsável".
 */
export function useTaskCreationOptions(
  enabled: boolean = true,
  assignedTo?: string | null,
) {
  const { currentTenant } = useAuth();
  // Módulo corrente (normal | retention | legal) derivado da rota
  const taskType = useTaskModuleType();

  const query = useQuery({
    queryKey: ['task-creation-options', currentTenant?.id, assignedTo || null, taskType],
    queryFn: async (): Promise<RpcResponse> => {
      // Cast `any` até types.ts ser regenerado com a nova assinatura
      const { data, error } = await (supabase.rpc as any)('get_task_creation_options', {
        p_assigned_to: assignedTo || null,
        p_tenant_hint: currentTenant?.id || null,
        p_task_type: taskType, // escopo do módulo corrente
      });

      if (error) {
        logger.error('Erro ao buscar opções de criação de tarefa', { error: error.message });
        throw error;
      }

      const payload = (data || {}) as Partial<RpcResponse>;
      return {
        categories: payload.categories || [],
        phases_by_category: payload.phases_by_category || {},
        tenant_has_phases: payload.tenant_has_phases ?? false,
        is_admin: payload.is_admin ?? false,
        // v1.3.0: padrões do módulo (podem não existir)
        default_category_id: payload.default_category_id ?? null,
        default_phase_id: payload.default_phase_id ?? null,
      };
    },
    enabled: enabled && !!currentTenant?.id,
    staleTime: 30_000,
  });

  const categories = query.data?.categories || [];
  const phasesByCategory = query.data?.phases_by_category || {};
  // v1.1.0: flags informativos vindos da RPC
  const tenantHasPhases = query.data?.tenant_has_phases ?? false;
  const isAdmin = query.data?.is_admin ?? false;
  // v1.3.0: categoria/fase padrão do módulo (null quando não configurado)
  const defaultCategoryId = query.data?.default_category_id ?? null;
  const defaultPhaseId = query.data?.default_phase_id ?? null;


  /** Retorna fases criáveis de uma categoria específica */
  const getPhases = useMemo(
    () => (categoryId: string | null | undefined): TaskCreationPhase[] => {
      if (!categoryId) return [];
      return phasesByCategory[categoryId] || [];
    },
    [phasesByCategory]
  );

  /** Existe pelo menos UMA fase criável em qualquer categoria? */
  const hasAnyCreatablePhase = useMemo(
    () => Object.values(phasesByCategory).some(arr => (arr?.length || 0) > 0),
    [phasesByCategory]
  );

  return {
    categories,
    getPhases,
    hasAnyCreatablePhase,
    // v1.1.0: novos flags
    tenantHasPhases,
    isAdmin,
    // v1.3.0: padrões do módulo (null = escolha livre, como hoje)
    defaultCategoryId,
    defaultPhaseId,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
